import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Coins, IndianRupee, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { createMoTopUpOrder, fetchWallet, verifyMoTopUp } from "@/lib/mapioApi";
import { useAuth } from "@/contexts/AuthContext";

const loadRazorpayCheckout = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = resolve;
    script.onerror = () => reject(new Error("Could not load Razorpay Checkout"));
    document.body.appendChild(script);
  });

export default function WalletModal({ open, onClose }) {
  const { user, refreshProfile } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [amountMo, setAmountMo] = useState(100);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  const quickAmounts = useMemo(() => [50, 100, 250, 500], []);

  useEffect(() => {
    if (!open || !user) return;

    setLoading(true);
    fetchWallet()
      .then(setWallet)
      .catch((err) => {
        console.error("Wallet load failed:", err);
        toast.error("Could not load MO wallet.");
      })
      .finally(() => setLoading(false));
  }, [open, user]);

  const handleTopUp = async () => {
    const mo = Number(amountMo);
    if (!Number.isInteger(mo) || mo < 1) {
      toast.error("Enter a valid MO amount.");
      return;
    }

    setPaying(true);
    try {
      const order = await createMoTopUpOrder(mo);
      if (!order.razorpay_key_id) {
        throw new Error("Razorpay key id is missing. Add it to backend/.env.");
      }

      await loadRazorpayCheckout();

      const checkout = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: "Mapix",
        description: `${order.amount_mo} MO top up`,
        order_id: order.order_id,
        prefill: {
          email: user.email,
          name: user.username,
        },
        theme: {
          color: "#2563eb",
        },
        handler: async (response) => {
          try {
            const updatedWallet = await verifyMoTopUp({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setWallet(updatedWallet);
            await refreshProfile();
            toast.success(`${order.amount_mo} MO added to your wallet.`);
          } catch (err) {
            console.error("MO verification failed:", err);
            toast.error(err?.response?.data?.detail || "Payment verification failed.");
          } finally {
            setPaying(false);
          }
        },
        modal: {
          ondismiss: () => setPaying(false),
        },
      });

      checkout.open();
    } catch (err) {
      console.error("MO top up failed:", err);
      toast.error(err?.response?.data?.detail || err.message || "Could not start payment.");
      setPaying(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[1000] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 16 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0, y: 16 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center shadow-md">
                  <Coins className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: "Manrope" }}>
                    MO Wallet
                  </h2>
                  <p className="text-xs text-gray-500">1 MO = ₹1</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              <div className="rounded-2xl bg-gray-950 text-white px-5 py-5">
                <p className="text-xs text-white/60">Available balance</p>
                <div className="mt-2 flex items-end gap-2">
                  <span className="text-4xl font-black tracking-normal" style={{ fontFamily: "Manrope" }}>
                    {loading ? "..." : wallet?.balance_mo ?? user?.mo_balance ?? 0}
                  </span>
                  <span className="pb-1 text-sm font-semibold text-white/70">MO</span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Top up amount
                </label>
                <div className="relative">
                  <IndianRupee className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="number"
                    min="1"
                    value={amountMo}
                    onChange={(e) => setAmountMo(e.target.value)}
                    className="w-full pl-10 pr-14 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition text-sm text-gray-900 outline-none"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-500">
                    MO
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {quickAmounts.map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      onClick={() => setAmountMo(amount)}
                      className="py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-xs font-semibold text-gray-700 transition"
                    >
                      {amount}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="button"
                disabled={paying}
                onClick={handleTopUp}
                className="w-full py-3.5 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 text-white font-semibold text-sm shadow-md hover:shadow-lg transition disabled:opacity-60 flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                {paying ? "Opening Razorpay..." : `Add ${amountMo || 0} MO`}
              </button>

              {wallet?.transactions?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-2">Recent activity</p>
                  <div className="max-h-36 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-xl">
                    {wallet.transactions.map((tx) => (
                      <div key={tx.id} className="px-3 py-2 flex items-center justify-between text-xs">
                        <span className="text-gray-600 capitalize">{tx.status} {tx.kind}</span>
                        <span className="font-bold text-gray-900">+{tx.amount_mo} MO</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
